// 회의록 목록 컬럼: 헤더(요약양식 편집)·새 노트/녹음·업로드 진행률·노트 목록·휴지통 목록.
// 필수 프레젠테이션: 상태·핸들러·ref는 MeetingsPage에서 props로 주입(동작 100% 동일).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconArrowsSort,
  IconChevronDown,
  IconFileText,
  IconFilter,
  IconFolder,
  IconMicrophone,
  IconPlus,
  IconSearch,
  IconTable,
  IconUpload,
  IconUser,
} from '@tabler/icons-react';
import { PillTabs } from '../../components/ui';
import {
  MEETING_THEME,
  meetingStatusChip,
  fmtDate,
  fmtListDate,
  fmtListDur,
  smBtn,
} from '../../utils/meetingUtil';

const T = MEETING_THEME;
const STATUS_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'queued', label: '대기' },
  { value: 'processing', label: '변환중' },
  { value: 'done', label: '완료' },
  { value: 'failed', label: '실패' },
];
// 서버 정렬(sort/order) 메뉴 — 값은 백엔드 MEETING_SORTS 와 같다.
const SORT_MENU = [
  { value: 'updated', label: '마지막 수정 일자' },
  { value: 'created', label: '생성 일자' },
  { value: 'recorded', label: '녹음 일자' },
  { value: 'title', label: '이름' },
  { value: 'duration', label: '길이' },
];
// 휴지통 정렬 메뉴 — 서버 인자가 없다(휴지통 응답은 통째로 온다) → 브라우저에서 정렬한다.
const TRASH_SORT_MENU = [
  { value: 'deleted', label: '삭제 일자' },
  { value: 'title', label: '이름' },
  { value: 'duration', label: '길이' },
  { value: 'created', label: '생성 일자' },
];
const defaultOrderFor = (value) => (value === 'title' ? 'asc' : 'desc');   // 이름만 오름차순 기본
const TRASH_SORT_DEFAULT = { key: 'deleted', dir: 'desc' };
// «전체 노트» 보기 탭(시안 F) — 시험이 탭 수를 이 정의에서 파생한다.
// 🔴 icon 은 tabler 컴포넌트 «참조»다(PillTabs renderIcon 이 그린다 · 문자열이면 글자가 그대로 찍힌다).
//    «폴더별 보기» 는 탭처럼 보이지만 누르면 폴더 서랍을 열고 선택 탭은 바꾸지 않는다.
export const NOTE_VIEW_TABS = [
  { key: 'all', label: '모든 회의', icon: IconTable },
  { key: 'mine', label: '내 회의', icon: IconUser },
  { key: 'folders', label: '폴더별 보기', icon: IconFolder },
];
// 날짜 그룹 행은 서버가 created_at 순으로 주는 정렬에서만 그린다(수정일 순이면 그룹이 쪼개진다).
const GROUPED_SORTS = ['created', 'recorded'];

function parseTime(value) {
  if (!value) return 0;
  const text = String(value);
  const ts = Date.parse(text.endsWith('Z') ? text : `${text}Z`);
  return Number.isFinite(ts) ? ts : 0;
}

// 휴지통 행 비교(오름차순 기준). 값이 같으면 0 — 안정 정렬이라 서버가 준 순서가 남는다(id 로 가르지 않는다).
function compareTrash(a, b, key) {
  if (key === 'title') return String(a.title || '').localeCompare(String(b.title || ''), 'ko');
  if (key === 'duration') return (Number(a.duration_sec) || 0) - (Number(b.duration_sec) || 0);
  const field = key === 'created' ? 'created_at' : 'deleted_at';
  return parseTime(a[field]) - parseTime(b[field]);
}

function compactText(value, max = 90) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function matchesStatus(m, filter) {
  if (filter === 'all') return true;
  if (filter === 'queued') return ['ready', 'uploaded', 'rediarize'].includes(m.status);
  if (filter === 'processing') return ['processing', 'diarizing'].includes(m.status);
  if (filter === 'done') return m.status === 'done';
  if (filter === 'failed') return ['failed', 'cancelled', 'cancelling'].includes(m.status);
  return true;
}

function meetingSource(m) {
  const raw = String(m.source || '').toLowerCase();
  if (raw === 'record') return 'record';
  if (raw === 'upload') return 'upload';
  return 'unknown';
}

function isRecent(m) {
  const ts = parseTime(m.created_at);
  return ts > 0 && Date.now() - ts < 7 * 24 * 60 * 60 * 1000;
}

// 날짜 그룹 — 날짜 칸(fmtListDate)과 같은 created_at · 같은 UTC→브라우저 로컬 변환. 값이 없으면 ''(그룹 없음).
const GROUP_WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
function groupKeyOf(iso) {
  const ts = parseTime(iso);
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// «오늘 · 10월 21일» / «어제 · 10월 20일» / 그 밖 «10월 19일 (일)».
export function groupLabelOf(iso, now = new Date()) {
  const ts = parseTime(iso);
  if (!ts) return '';
  const d = new Date(ts);
  const md = `${d.getMonth() + 1}월 ${d.getDate()}일`;
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) return `오늘 · ${md}`;
  if (sameDay(d, new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) return `어제 · ${md}`;
  return `${md} (${GROUP_WEEKDAYS[d.getDay()]})`;
}

// 지금 사용자 id(«내 회의» 거르기) — MeetingsPage 와 같은 localStorage 'user'. 깨진 JSON·id 없음이면 null.
function readMyId() {
  try {
    const id = JSON.parse(localStorage.getItem('user') || '{}')?.id;
    return id == null ? null : id;
  } catch {
    return null;
  }
}

// 휴지통 보관 일수 = /trash 응답 항목의 trash_days(서버 1~3650 정수). 양의 정수만 믿는다 —
// 문자열·0·NaN 이나 빈 휴지통이면 null(숫자를 지어내지 않는다).
function trashDaysOf(list) {
  const hit = (Array.isArray(list) ? list : []).find((m) => Number.isInteger(m?.trash_days) && m.trash_days > 0);
  return hit ? hit.trash_days : null;
}

function pillStyle(bg, color, border = 'transparent') {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    maxWidth: '100%',
    padding: '3px 8px',
    borderRadius: T.radius.pill,
    border: `1px solid ${border}`,
    background: bg,
    color,
    fontSize: T.font.xs,
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
  };
}

export default function MeetingList({
  mobile, trashView, sel, allView, folders, sort, order, onSortChange, fpEdit,
  setFpEdit, openFolderPrompt, fpText, setFpText, saveFolderPrompt,
  uploadPct, recordingBusy, startRec, onNewNote,
  setFolderDrawerOpen, trash, restoreMeeting, purgeMeeting,
  // 삭제된 폴더(GET /meetings/folders/trash · 삭제 묶음의 맨 위만) — 휴지통 보기에서 노트 목록 위에 묶어 보인다.
  folderTrash, restoreFolder,
  meetings, openMeeting, active, queueText, adminView,
  meetingsLoading, meetingsError, trashLoading, trashError,
  onBulkMove, onBulkTrash, onBulkRestore, onBulkPurge,
  // 노트 검색어는 MeetingsPage 가 갖는다(서버 q 로 거른다) — query=입력값 · resultQuery=지금 목록을 받아 온 요청의 검색어.
  query, onQueryChange, resultQuery,
}) {
  // 휴지통 검색어는 노트 검색어와 따로 둔다(노트 검색어가 휴지통으로 새지 않게).
  const [trashQuery, setTrashQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [menuOpen, setMenuOpen] = useState(null);   // 'sort' | 'filter' | 'trashSort' | null
  // 휴지통 정렬은 저장하지 않는다(보기를 바꾸면 기본 = 삭제 일자 최신순).
  const [trashSort, setTrashSort] = useState(TRASH_SORT_DEFAULT);
  // 여러 개 선택은 **이 컴포넌트가 갖는다**(부모 props 를 늘리지 않는다).
  // 담는 값은 String(m.id) — 서버 id(숫자)와 시험 id(문자)를 같은 자로 비교하기 위해서다.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // 보기 탭(«모든 회의»/«내 회의») — «전체 노트» 화면에서만 보이고 거른다(다른 화면에서는 남은 값이 거르지 않는다).
  const [noteTab, setNoteTab] = useState('all');
  const [myId] = useState(readMyId);   // 마운트 때 한 번 읽는다
  const menuWrapRef = useRef(null);
  const allBoxRef = useRef(null);
  const disabledWork = uploadPct != null || recordingBusy;
  const showNoteTabs = allView && !trashView && !sel;
  const mineOnly = showNoteTabs && noteTab === 'mine';
  // 목록 응답의 created_by 는 정수 · 사용자 id 는 문자열일 수도 있어 문자열로 맞춰 비교한다.
  const isMine = useCallback((m) => myId != null && String(m.created_by) === String(myId), [myId]);
  // 🔴 순서는 서버가 정한다(sort/order) — 여기서 다시 정렬하면 서버 정렬이 무용지물이 된다.
  //    상태 거르기만 브라우저에서 한다 · 글자 검색은 서버(q) — 목록 응답엔 전사·요약이 없어
  //    브라우저에서 거르면 전사·요약이 안 찾힌다.
  //    «내 회의» 도 여기서 거른다 — 그래야 전체 선택·묶음 동작(pickRows)도 걸러진 행만 대상이 된다.
  //    (목록 API 는 전량을 돌려준다 — 페이지 나누기가 생기면 서버 scope=mine 으로 바꿔야 한다.)
  const filteredMeetings = useMemo(() => {
    const rows = (Array.isArray(meetings) ? meetings : []).filter((m) => matchesStatus(m, statusFilter));
    return mineOnly ? rows.filter(isMine) : rows;
  }, [meetings, statusFilter, mineOnly, isMine]);
  // 내 노트 수(상태 필터 무시) — «내 회의» 머리 건수 알약 · 빈 문구 판정.
  const myCount = useMemo(() => (Array.isArray(meetings) ? meetings : []).filter(isMine).length, [meetings, isMine]);
  const mineEmpty = mineOnly && meetings.length > 0 && myCount === 0;
  // 휴지통 응답은 전사를 싣지 않는다 → 제목만 찾는다.
  const filteredTrash = useMemo(() => {
    const list = Array.isArray(trash) ? trash : [];
    const q = trashQuery.trim().toLowerCase();
    return q ? list.filter((m) => (m.title || '').toLowerCase().includes(q)) : list;
  }, [trash, trashQuery]);
  // 휴지통 순서는 브라우저가 정한다(거른 뒤 정렬 · 기본 삭제 일자 최신순). 🔴 복사 후 정렬 —
  // 검색어가 없으면 filteredTrash 가 trash prop 배열 그 자체라 제자리 정렬하면 원본이 바뀐다.
  const sortedTrash = useMemo(() => {
    const sign = trashSort.dir === 'asc' ? 1 : -1;
    return [...filteredTrash].sort((a, b) => sign * compareTrash(a, b, trashSort.key));
  }, [filteredTrash, trashSort]);
  const trashDays = useMemo(() => trashDaysOf(trash), [trash]);
  const deletedFolders = Array.isArray(folderTrash) ? folderTrash : [];
  const folderNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(folders) ? folders : []).forEach((f) => map.set(String(f.id), f.name));
    return map;
  }, [folders]);

  // 바깥 클릭·Esc 로 펼침 메뉴 닫기
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (!menuWrapRef.current?.contains(e.target)) setMenuOpen(null); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const sortKey = SORT_MENU.some((o) => o.value === sort) ? sort : 'updated';
  const sortDir = order === 'asc' ? 'asc' : 'desc';
  const sortLabel = SORT_MENU.find((o) => o.value === sortKey)?.label || '';
  const statusLabel = STATUS_FILTERS.find((o) => o.value === statusFilter)?.label || '';
  const pickSort = (value) => {
    // 고른 항목을 다시 누르면 방향 반전, 다른 항목은 그 항목의 기본 방향.
    const nextOrder = value === sortKey ? (sortDir === 'asc' ? 'desc' : 'asc') : defaultOrderFor(value);
    setMenuOpen(null);
    if (typeof onSortChange === 'function') onSortChange(value, nextOrder);
  };
  const pickStatus = (value) => { setStatusFilter(value); setMenuOpen(null); };
  const trashSortLabel = TRASH_SORT_MENU.find((o) => o.value === trashSort.key)?.label || '';
  const pickTrashSort = (value) => {
    // 일반 목록 pickSort 와 같은 규칙 — 고른 항목을 다시 누르면 방향 반전, 다른 항목은 그 항목의 기본 방향.
    setTrashSort((prev) => ({
      key: value,
      dir: value === prev.key ? (prev.dir === 'asc' ? 'desc' : 'asc') : defaultOrderFor(value),
    }));
    setMenuOpen(null);
  };

  // ── 여러 개 선택 ──────────────────────────────────────────────────────────
  // 선택 대상은 보기에 따라 하나 — 노트 목록이면 거른 노트, 휴지통이면 거르고 정렬한 휴지통 행(화면 순서).
  const pickRows = trashView ? sortedTrash : filteredMeetings;
  // 🔴 보기를 바꾸면 비운다(휴지통 검색어·휴지통 정렬·펼침 메뉴도 함께). sel 은 같은 폴더에서도 새 객체가 되므로
  //    (MeetingsPage saveFolderPrompt) sel 이 아니라 sel?.id 를 본다. key 로 다시 그리면
  //    statusFilter 까지 초기화된다(노트 검색어는 MeetingsPage 가 갖는다).
  useEffect(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
    setTrashQuery('');
    setTrashSort(TRASH_SORT_DEFAULT);
    setMenuOpen(null);
  }, [trashView, allView, sel?.id]);
  // 보이는 행으로 줄인다(검색·필터·목록 갱신). 🔴 바뀐 것이 없으면 **같은 Set** 을 돌려준다 —
  // 변환 중 노트가 있으면 4초마다 새 배열이 와서(휴지통도 다시 읽을 때마다 새 배열) 매번 새 Set 이면
  // 다시 그리기가 돈다.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(pickRows.map((m) => String(m.id)));
      const next = new Set();
      prev.forEach((key) => { if (visible.has(key)) next.add(key); });
      return next.size === prev.size ? prev : next;
    });
  }, [pickRows]);
  // 콜백에 넘길 id = 「보이는 행 ∩ 선택」을 다시 계산한 것(이중 방어) · 원래 id 타입을 지킨다.
  const visibleSelected = useMemo(
    () => pickRows.filter((m) => selectedIds.has(String(m.id))).map((m) => m.id),
    [pickRows, selectedIds],
  );
  const selectedCount = visibleSelected.length;
  const allVisibleSelected = pickRows.length > 0 && selectedCount === pickRows.length;
  const someVisibleSelected = selectedCount > 0 && !allVisibleSelected;
  const bulkBarOpen = selectedCount > 0;
  // 일부만 고른 상태(−) 는 React prop 이 없어 DOM 에 직접 준다.
  useEffect(() => {
    if (allBoxRef.current) allBoxRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected, bulkBarOpen]);
  const toggleRow = (id) => setSelectedIds((prev) => {
    const key = String(id);
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleAllVisible = () => setSelectedIds(
    () => (allVisibleSelected ? new Set() : new Set(pickRows.map((m) => String(m.id)))),
  );
  // 콜백은 Promise<boolean> — true 면 선택을 비우고 false 면 남긴다(이동 모달을 닫지 않은 경우 등).
  const runBulk = async (fn) => {
    if (typeof fn !== 'function' || bulkBusy) return;
    const ids = visibleSelected;
    if (ids.length === 0) return;
    setBulkBusy(true);
    let ok = false;
    try { ok = await fn(ids); } catch { ok = false; }
    finally { setBulkBusy(false); }
    if (ok) setSelectedIds(new Set());
  };

  // 🔴 minHeight:0 이 없으면 열 방향 flex 아이템의 min-height 가 auto(=내용 높이)로 풀려
  //    섹션이 「머리 + 카드 전부」 높이까지 커진다 → 안의 스크롤 박스가 줄어들 여지가 0 이라
  //    세로 스크롤이 아예 안 생기고 넘친 부분은 셸(SplitShell overflow:hidden)이 잘라 버린다.
  const sectionStyle = mobile
    ? { flex: 1, minWidth: 0, minHeight: 0, background: T.color.page, display: (active || adminView) ? 'none' : 'flex', flexDirection: 'column', position: 'relative' }
    : { flex: 1, minWidth: 0, minHeight: 0, background: T.color.page, display: 'flex', flexDirection: 'column', position: 'relative' };
  // 정렬·필터 단추 = 시안 F 보조 단추(높이 31 · 13px/500 · r5 · 그림자). hover 바탕은 클래스 gw-mmenu-btn.
  // 🔴 color 는 hex(T.color.*) 그대로 — 시험이 toHaveStyle 로 단언하고 jsdom 은 var(--…) 를 버린다.
  const menuBtnStyle = (on) => ({
    height: '31px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '0 11px',
    border: `1px solid ${on ? T.color.green : T.color.border}`,
    borderRadius: T.radius.md,
    background: T.color.surface,
    boxShadow: T.shadow.card,
    color: on ? T.color.greenDark : T.color.text,
    fontSize: T.font.base,
    fontWeight: 500,
    cursor: 'pointer',
    outline: 'none',
  });
  const menuPanelStyle = {
    position: 'absolute',
    top: '36px',
    left: 0,
    zIndex: 30,
    minWidth: '168px',
    padding: '4px',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${T.color.border}`,
    borderRadius: T.radius.md,
    background: T.color.surface,
    boxShadow: T.shadow.raised,
  };
  const menuItemStyle = (on) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '7px 9px',
    border: 'none',
    borderRadius: T.radius.sm,
    background: on ? T.color.selectedBg : 'transparent',
    color: on ? T.color.selectedText : T.color.text,
    fontSize: T.font.sm,
    fontWeight: on ? 800 : 600,
    textAlign: 'left',
    cursor: 'pointer',
  });
  // 모바일 [녹음][새 노트] — 시안 F .btn(높이 31 · r5 · 13/500 · 그림자). 바탕·글자·테두리는 단추마다 준다
  // (녹음 = 보조 흰 바탕 · 새 노트 = 주 파랑). flex 1 1 122px 는 시험·e2e 가 단언한다.
  const noteActBtn = {
    height: '31px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '0 11px',
    borderRadius: T.radius.md,
    boxShadow: T.shadow.card,
    fontSize: T.font.base,
    fontWeight: 500,
    flex: '1 1 122px',
    opacity: disabledWork ? 0.5 : 1,
    cursor: disabledWork ? 'not-allowed' : 'pointer',
  };
  const iconSize = 15;
  // 행 체크박스는 평소 안 보인다 — 숨김은 **클래스 CSS** 로만 한다.
  // 🔴 inline opacity:0 을 주면 `:hover` 규칙을 이겨 영구히 안 보이고,
  //    display/visibility 로 숨기면 키보드·시험·e2e 가 못 누른다(폴더 행 .gw-mrow-add 선례와 같다).
  const pickStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingTop: '9px',
    cursor: 'pointer',
  };
  // 휴지통 행 체크박스 — 늘 보인다(hover 숨김 클래스 없음). 제목 줄에 맞춰 2px 로 덮는다.
  // (노트 표 행은 ① 칸 안에서 가운데 정렬이라 0 으로 덮는다.)
  const trashPickStyle = { ...pickStyle, paddingTop: '2px' };
  const searchIconStyle = { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: T.color.muted, pointerEvents: 'none' };
  const searchInputStyle = { width: '100%', height: '36px', boxSizing: 'border-box', padding: '0 10px 0 32px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.surface, color: T.color.text, fontSize: T.font.sm, outline: 'none' };
  const bulkBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '10px',
    padding: '7px 9px',
    border: `1px solid ${T.color.border}`,
    borderRadius: T.radius.md,
    background: T.color.subtle,
  };
  // 보기 탭 줄 — 데스크톱은 제목 줄 아래, 모바일은 [녹음][새 노트] 줄 아래(둘 다 검색 상자 위).
  // 줄 간격·아래 선은 이 감싸개가 갖는다(모바일은 좁게) — 공용 .gw-tabs(index.html)의 같은 간격·선은
  // 위 <style> 의 [data-gw-noteview] .gw-tabs 규칙이 0 으로 덮는다(겹치면 선이 두 줄이 된다).
  // tabpanel 은 목록 스크롤 칸이다(아래 scrollPanelAttrs).
  const noteViewTabs = showNoteTabs ? (
    <div data-gw-noteview="" style={{ display: 'flex', alignItems: 'center', ...(mobile ? { marginBottom: '10px', paddingBottom: '8px' } : { minHeight: '34px', marginBottom: '22px', paddingBottom: '11px' }), borderBottom: `1px solid ${T.color.borderSoft}` }}>
      <PillTabs ariaLabel="노트 보기" idBase="notes-view" items={NOTE_VIEW_TABS} value={noteTab}
        onChange={(k) => (k === 'folders' ? setFolderDrawerOpen?.(true) : setNoteTab(k))} />
    </div>
  ) : null;
  const scrollPanelAttrs = showNoteTabs
    ? { role: 'tabpanel', id: `notes-view-panel-${noteTab}`, 'aria-labelledby': `notes-view-tab-${noteTab}` }
    : {};
  const showGroups = GROUPED_SORTS.includes(sortKey);
  // 폴더 칸 — 시안 Tag(12px · 2px 6px · r3 · subtle 바탕).
  const folderTagStyle = { display: 'inline-block', maxWidth: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '2px 6px', borderRadius: '3px', background: T.color.subtle, color: T.color.slate, fontSize: T.font.sm };

  return (
    <>
      {/* 노트 «표»(시안 F) — 행은 article.gw-mnote 그대로 두고 CSS 격자로 6열을 그린다(진짜 <table> 아님).
          모바일 배치(3열 격자 · 머리 없음)는 스크롤 칸의 data-gw-ntable="m"(앱 mobile 판정)에 건다.
          hover 바탕은 행 인라인 바탕(surface)을 이기려고 !important. */}
      <style>{`.gw-mnote-pick{opacity:0;transition:opacity .15s}.gw-mnote:hover .gw-mnote-pick,.gw-mnote:focus-within .gw-mnote-pick,.gw-mnote-pick:focus-within{opacity:1}.gw-mmenu-btn:hover:not(:disabled){background:var(--gw-hover,#efefed)!important}`
        + '.gw-ntable-head,.gw-ntable-row,.gw-ngroup{box-sizing:border-box}'
        + '.gw-ntable-head,.gw-ntable-row{display:grid;grid-template-columns:38% 14% 9% 13% 13% 13%;align-items:center;border-bottom:1px solid var(--gw-line,#eeeeec)}'
        + '.gw-ntable-head{height:33px;font-size:12px;font-weight:400;color:var(--gw-muted,#92918d)}'
        + '.gw-ntable-head>span::before{content:attr(data-label)}'
        + '.gw-ntable-head>span,.gw-ncell{padding:0 9px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'
        + '.gw-ntable-row{min-height:47px;font-size:12px;color:var(--gw-secondary,#64635f)}'
        + '.gw-ntable-row:hover{background:var(--gw-subtle,#fbfbfa)!important}'
        + '.gw-ncell{display:flex;align-items:center;gap:6px}'
        + '.gw-ncell.gw-ncell--txt{display:block}'
        + '.gw-ncell-full{grid-column:1/-1;padding:0 9px 8px}'
        + '.gw-ngroup{height:39px;display:flex;align-items:flex-end;padding:0 4px 6px;font-size:11px;color:var(--gw-muted,#92918d);border-bottom:1px solid var(--gw-line,#eeeeec)}'
        + '.gw-nsum{color:var(--gw-blue-ink,#356c9b);font-size:11px;flex-shrink:0}'
        + '@media (min-width:901px) and (max-width:1380px){.gw-ntable-head,.gw-ntable-row{grid-template-columns:27% 20% 12% 13% 14% 14%}}'
        + '@media (min-width:601px) and (max-width:900px){.gw-ntable-head,.gw-ntable-row{grid-template-columns:35% 14% 10% 13% 14% 14%}}'
        + '[data-gw-ntable="m"] .gw-ntable-row{grid-template-columns:repeat(3,minmax(0,1fr));gap:7px 0;padding:13px 0}'
        + '[data-gw-ntable="m"] .gw-ntable-row>.gw-ncell:first-child{grid-column:1/-1;margin-bottom:4px;font-size:13px}'
        + '[data-gw-ntable="m"] .gw-ntable-row>.gw-ncell{padding:0 5px;font-size:10px}'
        + '[data-gw-ntable="m"] .gw-ntable-row>.gw-ncell:nth-child(5){grid-column:2}'
        + '[data-gw-ntable="m"] .gw-ntable-row>.gw-ncell:nth-child(6){grid-column:3}'
        + '[data-gw-ntable="m"] .gw-ngroup{padding:15px 4px 9px;height:auto}'
        + '[data-gw-noteview] .gw-tabs{min-height:0;padding-bottom:0;margin-bottom:0;border-bottom:0}'}</style>
      {/* 회의록 목록 */}
      <section style={sectionStyle}>
        {/* 머리(폴더 이름·새 노트/녹음·검색·정렬/필터)는 스크롤해도 제자리 — flexShrink:0 만 준다.
            🔴 overflow 를 주면 안 된다(정렬/필터 펼침 메뉴가 position:absolute 라 잘린다). */}
        <div style={{ flexShrink: 0, padding: mobile ? '12px' : '14px', borderBottom: `1px solid ${T.color.borderSoft}`, background: T.color.surface }}>
          <div style={{ fontWeight: 800, fontSize: T.font.lg, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {mobile && <button onClick={() => setFolderDrawerOpen(true)} title="폴더 목록" style={{ ...smBtn(T.color.slate), padding: '5px 9px', display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}><IconFolder size={iconSize} />폴더</button>}
            {/* 🔴 머리 DOM 의 첫 span 이 제목이다(e2e 가 그 자리로 읽는다) */}
            <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: T.font.md, fontWeight: 620, letterSpacing: '-0.18px' }}>{trashView ? '휴지통' : (sel ? sel.name : (allView ? '전체 노트' : '미분류'))}</span>
            {/* 건수 — «내 회의» 면 받은 목록 중 내 노트 수(상태 필터 무시 · 다른 화면의 건수와 같은 기준) */}
            {!trashView && <span style={pillStyle('#f1f1ef', '#64635f')}>{mineOnly ? myCount : meetings.length}</span>}
            {!trashView && sel && (sel.role === 'owner' || sel.role === 'edit') && (
              <button onClick={openFolderPrompt} title="이 폴더의 요약 양식 설정" style={{ marginLeft: 'auto', fontSize: T.font.xs, background: T.color.surface, border: `1px solid ${T.color.outlineBtnBorder}`, color: T.color.text, borderRadius: T.radius.pill, padding: '5px 10px', cursor: 'pointer', fontWeight: 700 }}>요약 양식</button>
            )}
          </div>
          {!mobile && noteViewTabs}
          {!trashView && fpEdit && sel && (
            <div style={{ marginBottom: '12px', padding: '10px', background: T.color.subtle, borderRadius: T.radius.md, border: `1px solid ${T.color.border}` }}>
              <div style={{ fontSize: T.font.xs, color: T.color.muted, marginBottom: '6px' }}>이 폴더에서 사용할 요약 양식을 입력하십시오.</div>
              <textarea value={fpText} onChange={(e) => setFpText(e.target.value)} rows={5} placeholder="예: 안건 / 결정사항 / 액션 아이템 / 최종요약 형식으로 작성하십시오."
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm, fontSize: T.font.sm, fontFamily: 'inherit', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <button onClick={saveFolderPrompt} style={smBtn(T.color.green)}>저장</button>
                <button onClick={() => setFpEdit(false)} style={smBtn(T.color.slate)}>취소</button>
              </div>
            </div>
          )}
          {trashView ? (
            <>
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <IconSearch size={16} style={searchIconStyle} />
                <input value={trashQuery} onChange={(e) => setTrashQuery(e.target.value)}
                  placeholder="휴지통 검색" aria-label="휴지통 검색" style={searchInputStyle} />
              </div>
              {/* 휴지통 정렬 ⇅ — 모양·고르기 규칙은 일반 목록 정렬 단추와 같고 정렬은 브라우저가 한다.
                  🔴 menuWrapRef 를 여기에도 단다 — 없으면 항목을 누르는 mousedown 에 바깥 누름으로 보고 먼저 닫힌다
                     (두 가지는 동시에 그려지지 않으니 같은 ref 를 쓴다). */}
              <div ref={menuWrapRef} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <div style={{ position: 'relative' }}>
                  <button type="button" className="gw-mmenu-btn" title="휴지통 정렬" aria-label="휴지통 정렬" aria-haspopup="menu" aria-expanded={menuOpen === 'trashSort'}
                    onClick={() => setMenuOpen((v) => (v === 'trashSort' ? null : 'trashSort'))} style={menuBtnStyle(false)}>
                    <IconArrowsSort size={15} />
                    <span style={{ minWidth: 0 }}>{trashSortLabel}</span>
                    <span aria-hidden="true" style={{ color: T.color.muted }}>{trashSort.dir === 'asc' ? '↑' : '↓'}</span>
                  </button>
                  {menuOpen === 'trashSort' && (
                    <div role="menu" aria-label="휴지통 정렬 기준" style={menuPanelStyle}>
                      {TRASH_SORT_MENU.map((o) => (
                        <button key={o.value} type="button" role="menuitemradio" aria-checked={o.value === trashSort.key}
                          onClick={() => pickTrashSort(o.value)} style={menuItemStyle(o.value === trashSort.key)}>
                          <span>{o.label}</span>
                          {o.value === trashSort.key && <span aria-hidden="true">{trashSort.dir === 'asc' ? '↑' : '↓'}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* 보관 기간은 서버 값(trash_days) — 관리자가 바꾸면 이 문구도 따라간다 */}
              <div style={{ ...pillStyle(T.color.subtle, T.color.muted, T.color.border), whiteSpace: 'normal', fontWeight: 600 }}>
                {trashDays
                  ? `휴지통에 넣은 노트는 ${trashDays}일이 지나면 자동으로 영구 삭제됩니다.`
                  : '휴지통에 넣은 노트는 보관 기간이 지나면 자동으로 영구 삭제됩니다.'}
              </div>
            </>
          ) : (
            <>
              {/* 모바일 진입점 — [녹음](즉시 녹음 · 보조) · [새 노트](새 노트 화면 · 주) 순서(시안 F).
                  업로드·화자분리는 «새 노트» 화면에서 고른다(클로바 묶음 B4c-2). */}
              {mobile && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <button onClick={startRec} disabled={disabledWork} style={{ ...noteActBtn, background: T.color.surface, color: T.color.text, border: `1px solid ${T.color.border}` }}><IconMicrophone size={iconSize} />녹음</button>
                  <button onClick={onNewNote} disabled={disabledWork} style={{ ...noteActBtn, background: T.color.green, color: 'white', border: `1px solid ${T.color.green}` }}><IconPlus size={iconSize} />새 노트</button>
                </div>
              )}
              {mobile && noteViewTabs}
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <IconSearch size={16} style={searchIconStyle} />
                <input value={query || ''} onChange={(e) => onQueryChange?.(e.target.value)} placeholder="제목·전사·요약 검색"
                  maxLength={100} style={searchInputStyle} />
              </div>
              {/* 정렬 ⇅ · 필터 ▽ — select 2개를 펼침 메뉴 단추로 바꿨다 */}
              <div ref={menuWrapRef} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: mobile && uploadPct != null ? '8px' : 0 }}>
                <div style={{ position: 'relative' }}>
                  <button type="button" className="gw-mmenu-btn" title="정렬" aria-label="정렬" aria-haspopup="menu" aria-expanded={menuOpen === 'sort'}
                    onClick={() => setMenuOpen((v) => (v === 'sort' ? null : 'sort'))} style={menuBtnStyle(false)}>
                    <IconArrowsSort size={15} />
                    <span style={{ minWidth: 0 }}>{sortLabel}</span>
                    <span aria-hidden="true" style={{ color: T.color.muted }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                  </button>
                  {menuOpen === 'sort' && (
                    <div role="menu" aria-label="정렬 기준" style={menuPanelStyle}>
                      {SORT_MENU.map((o) => (
                        <button key={o.value} type="button" role="menuitemradio" aria-checked={o.value === sortKey}
                          onClick={() => pickSort(o.value)} style={menuItemStyle(o.value === sortKey)}>
                          <span>{o.label}</span>
                          {o.value === sortKey && <span aria-hidden="true">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <button type="button" className="gw-mmenu-btn" title="필터" aria-label="필터" aria-haspopup="menu" aria-expanded={menuOpen === 'filter'}
                    onClick={() => setMenuOpen((v) => (v === 'filter' ? null : 'filter'))} style={menuBtnStyle(statusFilter !== 'all')}>
                    <IconFilter size={15} />
                    {statusFilter !== 'all' && (
                      <>
                        <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: T.color.green }} />
                        <span style={{ minWidth: 0 }}>{statusLabel}</span>
                      </>
                    )}
                  </button>
                  {menuOpen === 'filter' && (
                    <div role="menu" aria-label="상태 필터" style={menuPanelStyle}>
                      {STATUS_FILTERS.map((o) => (
                        <button key={o.value} type="button" role="menuitemradio" aria-checked={o.value === statusFilter}
                          onClick={() => pickStatus(o.value)} style={menuItemStyle(o.value === statusFilter)}>
                          <span>{o.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {mobile && uploadPct != null && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.font.xs, color: T.color.muted, marginBottom: '4px' }}>
                    <span>업로드 중…</span><span>{uploadPct}%</span>
                  </div>
                  <div style={{ height: '5px', background: T.color.borderSoft, borderRadius: T.radius.pill, overflow: 'hidden' }}>
                    <div style={{ width: `${uploadPct}%`, height: '100%', background: T.color.green, transition: 'width .2s' }} />
                  </div>
                </div>
              )}
            </>
          )}
          {/* 여러 개 선택 동작 줄 — 하나 이상 고르면 머리 div 안쪽 맨 끝에 보인다.
              휴지통은 [복원][영구 삭제], 노트 목록은 [노트 이동][휴지통으로 이동].
              🔴 휴지통 행에도 한 건 [복원] 이 있어 같은 이름이 겹친다 → 동작 줄 단추는 aria-label 로 구별한다. */}
          {bulkBarOpen && (
            <div style={bulkBarStyle}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: T.font.sm, fontWeight: 700, color: T.color.text, cursor: 'pointer' }}>
                <input ref={allBoxRef} type="checkbox" aria-label="전체 선택" checked={allVisibleSelected}
                  onChange={toggleAllVisible} style={{ margin: 0, flexShrink: 0, accentColor: T.color.green, cursor: 'pointer' }} />
                <span>{`${selectedCount}개 선택`}</span>
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {trashView ? (
                  <>
                    {typeof onBulkRestore === 'function' && (
                      <button type="button" aria-label="선택한 노트 복원" disabled={bulkBusy} onClick={() => runBulk(onBulkRestore)}
                        style={{ ...smBtn(T.color.green), opacity: bulkBusy ? 0.6 : 1 }}>복원</button>
                    )}
                    {typeof onBulkPurge === 'function' && (
                      <button type="button" aria-label="선택한 노트 영구 삭제" disabled={bulkBusy} onClick={() => runBulk(onBulkPurge)}
                        style={{ ...smBtn(T.color.slate), color: T.color.danger, opacity: bulkBusy ? 0.6 : 1 }}>영구 삭제</button>
                    )}
                  </>
                ) : (
                  <>
                    {typeof onBulkMove === 'function' && (
                      <button type="button" disabled={bulkBusy} onClick={() => runBulk(onBulkMove)}
                        style={{ ...smBtn(T.color.slate), opacity: bulkBusy ? 0.6 : 1 }}>노트 이동</button>
                    )}
                    {typeof onBulkTrash === 'function' && (
                      <button type="button" disabled={bulkBusy} onClick={() => runBulk(onBulkTrash)}
                        style={{ ...smBtn(T.color.slate), color: T.color.danger, opacity: bulkBusy ? 0.6 : 1 }}>휴지통으로 이동</button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        {/* 세로 스크롤 주인 — 노트 목록·휴지통 목록이 이 한 박스를 함께 쓴다.
            모바일에서만 'gw-tabbar-pad'(MobileTabBar 가 심는 !important 규칙)로 하단 탭바 높이만큼
            여백을 받아 마지막 카드가 탭바에 깔리지 않는다(데스크톱엔 그 규칙 자체가 없다).
            시안 표는 행 사이 간격이 없다 → 위아래 여백 0(휴지통 카드도 같은 칸을 쓴다).
            data-gw-ntable = 표 배치(d=6열 · m=3열 격자) — 머리 유무와 같은 mobile 값으로 정한다. */}
        <div
          data-testid="meeting-list-scroll"
          className={mobile ? 'gw-tabbar-pad' : undefined}
          data-gw-ntable={mobile ? 'm' : 'd'}
          {...scrollPanelAttrs}
          style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: mobile ? '0 16px' : '0 14px' }}
        >
          {trashView ? (
            <>
              {/* 삭제된 폴더 — 되돌리면 함께 지운 하위 폴더와 그 삭제로 휴지통에 간 노트가 함께 돌아온다.
                  🔴 단추 글자에 «복원» 을 쓰지 않는다(«되돌리기» · aria-label «폴더 복원») — e2e(test_b1)가
                     «복원» 글자 단추가 있으면 «영구삭제» 단추도 있어야 한다고 본다. 노트가 없을 때 빈 문구는 그대로다. */}
              {deletedFolders.length > 0 && (
                <div data-testid="trash-folder-group" style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: T.font.xs, fontWeight: 800, color: T.color.muted, padding: '2px 4px 6px' }}>삭제된 폴더</div>
                  {deletedFolders.map((f) => (
                    <div key={`folder-${f.id}`} data-testid="trash-folder-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, padding: '10px 12px', borderRadius: T.radius.md, marginBottom: '6px', background: T.color.surface, border: `1px dashed ${T.color.border}` }}>
                      <IconFolder size={16} style={{ flexShrink: 0, color: T.color.muted }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.font.base, fontWeight: 800, color: T.color.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                        <div style={{ fontSize: T.font.xs, color: T.color.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          삭제 {fmtDate(f.deleted_at)}{(f.folder_count > 0 || f.note_count > 0) ? ` · 하위 폴더 ${f.folder_count || 0}개 · 노트 ${f.note_count || 0}개` : ''}
                        </div>
                      </div>
                      {typeof restoreFolder === 'function' && (
                        <button type="button" aria-label="폴더 복원" title="폴더 복원" onClick={() => restoreFolder(f)}
                          style={{ ...smBtn(T.color.green), padding: '5px 10px', flexShrink: 0 }}>되돌리기</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {trashLoading && trash.length === 0 && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>휴지통을 불러오는 중입니다.</div>}
              {trashError && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: T.radius.md, fontSize: T.font.base, textAlign: 'center', padding: '14px', margin: '6px' }}>{trashError}</div>}
              {!trashError && sortedTrash.map((m) => {
                const picked = selectedIds.has(String(m.id));
                return (
                  <div key={m.id} style={{ padding: '12px', borderRadius: T.radius.md, marginBottom: '8px', background: picked ? T.color.selectedBg : T.color.surface, border: `1px solid ${picked ? T.color.outlineBtnBorder : T.color.border}`, boxShadow: T.shadow.card }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
                      {/* 휴지통 행 체크박스는 늘 보인다 — 노트 행의 hover 숨김 클래스(gw-mnote-pick)를 쓰지 않는다
                          (그 CSS 는 부모 .gw-mnote 에 걸리고, 여기에 gw-mnote 를 달면 e2e 의 노트 행 선택자가 흐려진다). */}
                      <label style={trashPickStyle}>
                        <input type="checkbox" aria-label="노트 선택" checked={picked}
                          onChange={() => toggleRow(m.id)}
                          style={{ margin: 0, flexShrink: 0, accentColor: T.color.green, cursor: 'pointer' }} />
                      </label>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.font.base, fontWeight: 800, color: T.color.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title || '(제목 없음)'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: T.font.xs, color: T.color.muted }}>삭제 {fmtDate(m.deleted_at)}</span>
                          <button onClick={() => restoreMeeting(m)} style={{ ...smBtn(T.color.green), marginLeft: 'auto', padding: '5px 10px' }}>복원</button>
                          <button onClick={() => purgeMeeting(m)} style={{ ...smBtn('#ef4444'), padding: '5px 10px' }}>영구삭제</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!trashLoading && !trashError && trash.length > 0 && filteredTrash.length === 0 && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>검색 조건에 맞는 노트가 없습니다.</div>}
              {!trashLoading && !trashError && trash.length === 0 && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>휴지통이 비어 있습니다.</div>}
            </>
          ) : (<>
            {/* 표 머리(데스크톱만) — 글자는 CSS content:attr(data-label) 로 그린다(DOM 글자면 행의 «AI 요약» 과 겹쳐
                getByText 가 둘을 찾는다). 스크롤 칸의 첫 자식 · 마지막 자식은 그대로 마지막 행이다. */}
            {!mobile && (
              <div className="gw-ntable-head" aria-hidden="true">{['회의', '날짜', '길이', '출처', 'AI 요약', '폴더'].map((h) => <span key={h} data-label={h} />)}</div>
            )}
            {meetingsLoading && meetings.length === 0 && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>노트 목록을 불러오는 중입니다.</div>}
            {meetingsError && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: T.radius.md, fontSize: T.font.base, textAlign: 'center', padding: '14px', margin: '6px' }}>{meetingsError}</div>}
            {!meetingsError && filteredMeetings.map((m, idx) => {
              const st = meetingStatusChip(m);   // 전사 0자면 '완료' 대신 '소리 없음'
              const source = meetingSource(m);
              const SourceIcon = source === 'upload' ? IconUpload : IconMicrophone;
              const iconColor = source === 'record' ? '#426b55' : source === 'upload' ? '#356c9b' : T.color.iconLine;
              // 날짜 그룹 행 — 작성일·녹음 일자 정렬에서만 · 앞 행과 날짜(created_at)가 달라질 때 그 행 앞에 넣는다.
              const groupKey = showGroups ? groupKeyOf(m.created_at) : '';
              const groupLabel = groupKey && (idx === 0 || groupKeyOf(filteredMeetings[idx - 1].created_at) !== groupKey)
                ? groupLabelOf(m.created_at)
                : '';
              const sourceLabel = source === 'record' ? '녹음' : source === 'upload' ? '업로드' : '생성 경로 미상';
              // 목록 응답은 transcript_text·summary 를 싣지 않는다(상세 전용) → 서버가 잘라 준 preview 가 1순위.
              const preview = compactText(m.preview || m.transcript_text || m.summary);
              const summaryHead = compactText(m.summary_head);
              // 목록 응답엔 폴더 이름이 없다 → folders prop 에서 folder_id 로 찾는다(못 찾으면 숨김).
              const folder = m.folder_id == null
                ? ''
                : (folderNameById.get(String(m.folder_id)) || m.folder_name || m.folder?.name || '');
              const listDate = fmtListDate(m.created_at);
              const listDur = fmtListDur(m.duration_sec);
              const picked = selectedIds.has(String(m.id));
              const highlighted = picked || active?.id === m.id;
              // 좁은 화면이거나 하나라도 고른 뒤에는 늘 보인다(그때만 inline opacity:1 을 준다).
              const pickAlwaysOn = !!mobile || selectedCount > 0;
              return (
                <Fragment key={m.id}>
                  {groupLabel && (
                    <div className="gw-ngroup" role="presentation">
                      <IconChevronDown size={12} aria-hidden="true" style={{ marginRight: '4px' }} />{groupLabel}
                    </div>
                  )}
                  {/* 표 행 = 6칸(회의·날짜·길이·출처·AI 요약·폴더) + 진행률·«중지 중…» 줄(gw-ncell-full · 격자 1/-1).
                      카드 테두리·그림자·모서리·간격은 없다(아래 선은 CSS) · 바탕은 인라인(선택 표시 · 시험이 surface 를 단언). */}
                  <article className="gw-mnote gw-ntable-row" onClick={() => openMeeting(m)}
                    style={{ cursor: 'pointer', background: highlighted ? T.color.selectedBg : T.color.surface }}>
                    {/* ① 회의 — 체크박스 · 파일 아이콘 · 본문 칸(제목 줄 + 둘째 줄).
                        🔴 본문 칸 묶음(제목 div → 제목 줄 div → 본문 칸 div)은 구조를 바꾸지 않는다 —
                           시험이 제목의 parentElement.parentElement 를 본문 칸으로 보고 자식 수·미리보기를 단언한다.
                        🔴 체크박스 감싸개는 label(보통 흐름 안 · 절대 위치로 행을 덮으면 e2e 의 행 클릭이 막힌다). */}
                    <div className="gw-ncell">
                      <label onClick={(e) => e.stopPropagation()}
                        className={pickAlwaysOn ? undefined : 'gw-mnote-pick'}
                        style={{ ...pickStyle, paddingTop: 0, opacity: pickAlwaysOn ? 1 : undefined }}>
                        <input type="checkbox" aria-label="노트 선택" checked={picked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { e.stopPropagation(); toggleRow(m.id); }}
                          style={{ margin: 0, flexShrink: 0, accentColor: T.color.green, cursor: 'pointer' }} />
                      </label>
                      <IconFileText size={16} aria-hidden="true" style={{ flexShrink: 0, color: T.color.faint }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                          <div style={{ fontSize: T.font.base, fontWeight: 500, color: T.color.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m.title || '(제목 없음)'}</div>
                          {isRecent(m) && <span title="최근 노트" style={pillStyle('#f1f1ef', '#64635f')}>N</span>}
                        </div>
                        {/* 둘째 줄 — 요약이 있으면 「AI 요약 · …」, 없으면 전사 미리보기 */}
                        {summaryHead ? (
                          <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, fontSize: T.font.sm }}>
                            <span style={{ flexShrink: 0, color: T.color.text, fontWeight: 800 }}>AI 요약</span>
                            <span aria-hidden="true" style={{ flexShrink: 0, color: T.color.faint }}>·</span>
                            <span style={{ minWidth: 0, color: T.color.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summaryHead}</span>
                          </div>
                        ) : (
                          preview && <div style={{ marginTop: '3px', fontSize: T.font.sm, color: T.color.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</div>
                        )}
                      </div>
                    </div>
                    {/* ② 날짜 · ③ 길이 */}
                    <div className="gw-ncell gw-ncell--txt" style={{ fontVariantNumeric: 'tabular-nums' }}>{listDate || '-'}</div>
                    <div className="gw-ncell gw-ncell--txt" style={{ fontVariantNumeric: 'tabular-nums' }}>{listDur || '-'}</div>
                    {/* ④ 출처 — 아이콘(원 없음) + 글자 */}
                    <div className="gw-ncell">
                      <span role="img" aria-label={sourceLabel} title={sourceLabel} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: iconColor }}>
                        <SourceIcon size={14} />
                      </span>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{sourceLabel}</span>
                    </div>
                    {/* ⑤ AI 요약 — 완료가 아니면(또는 소리 없음) 상태 알약·대기열 · 완료면 요약 유무.
                        🔴 «AI 요약» 글자는 ① 칸 둘째 줄에만 둔다(여기에 또 쓰면 getByText 가 둘을 찾는다). */}
                    <div className="gw-ncell">
                      {(m.no_speech || m.status !== 'done') ? (
                        <>
                          <span style={pillStyle(st.bg, st.color)}>{st.label}</span>
                          {queueText(m) && <span style={pillStyle(T.color.subtle, T.color.muted, T.color.border)}>{queueText(m)}</span>}
                        </>
                      ) : summaryHead ? (
                        <span className="gw-nsum">요약 있음</span>
                      ) : (
                        <span style={{ color: T.color.faint }}>요약 전</span>
                      )}
                    </div>
                    {/* ⑥ 폴더 — 마지막 폴더 이름만(경로 아님) · 긴 이름은 말줄임 · 없으면 빈 칸 */}
                    <div className="gw-ncell">
                      {folder && <span title={folder} style={folderTagStyle}>{folder}</span>}
                    </div>
                    {m.status === 'processing' && (
                      /* #139 전사 진행률: 워커가 세그먼트별 갱신(progress 0~100), 4초 폴링으로 반영 */
                      <div className="gw-ncell-full">
                        <div style={{ height: '4px', background: T.color.borderSoft, borderRadius: T.radius.pill, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max(3, m.progress || 0)}%`, height: '100%', background: T.color.green, transition: 'width .4s' }} />
                        </div>
                        <div style={{ fontSize: '10px', color: T.color.muted, marginTop: '3px' }}>
                          {(m.progress || 0) >= 99 ? '화자 분석 중 (몇 분 소요될 수 있음)' : ((m.progress || 0) > 0 ? `전사 중 ${m.progress}%` : '전사 준비 중…')}
                        </div>
                      </div>
                    )}
                    {m.status === 'cancelling' && <div className="gw-ncell-full" style={{ fontSize: '10px', color: '#9a3412' }}>중지 중…</div>}
                  </article>
                </Fragment>
              );
            })}
            {/* «내 회의» 로 거른 결과가 0건(받은 목록은 있음) — 아래 «검색 조건…» 문구와 겹치지 않게 그쪽 조건에서 뺀다. */}
            {!meetingsLoading && !meetingsError && mineEmpty && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>내가 만든 노트가 없습니다.</div>}
            {!meetingsLoading && !meetingsError && meetings.length > 0 && filteredMeetings.length === 0 && !mineEmpty && <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>검색 조건에 맞는 노트가 없습니다.</div>}
            {/* 빈 문구는 「그 목록을 받아 온 요청의 검색어」(resultQuery)로 고른다 — 입력값(query)으로 고르면
                검색어를 지운 뒤 응답이 오기 전 300ms 동안 «노트가 없습니다…» 가 깜빡인다. */}
            {!meetingsLoading && !meetingsError && meetings.length === 0 && (resultQuery
              ? <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>검색 결과가 없습니다.</div>
              : <div style={{ color: T.color.muted, fontSize: T.font.base, textAlign: 'center', padding: '24px' }}>노트가 없습니다.<br />파일 업로드 또는 녹음으로 추가하십시오.</div>)}
          </>)}
        </div>
      </section>
    </>
  );
}
